import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

const Student = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [learningPaths, setLearningPaths] = useState([]);
  const [homework, setHomework] = useState([]);
  const [progress, setProgress] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseVideos, setCourseVideos] = useState([]);
  const [courseHomework, setCourseHomework] = useState([]);

  // Chat functionality
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);

  useEffect(() => {
    fetchEnrolledCourses();
    fetchAvailableCourses();
    fetchLearningPaths();
    fetchHomework();
  }, []);

  const fetchEnrolledCourses = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get('${config.API_BASE_URL}/api/enrollments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrolledCourses(response.data);
    } catch (error) {
      console.error('Error fetching enrolled courses', error);
      // Fallback to sample data if API fails
      setEnrolledCourses([
        { id: 1, title: 'Mathematics Fundamentals', description: 'Basic mathematics concepts and problem solving', status: 'active' },
        { id: 2, title: 'Introduction to Physics', description: 'Fundamental principles of physics', status: 'active' }
      ]);
    }
  };

  const fetchAvailableCourses = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get('${config.API_BASE_URL}/api/enrollments/available', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableCourses(response.data);
    } catch (error) {
      console.error('Error fetching available courses', error);
    }
  };

  const fetchLearningPaths = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get('${config.API_BASE_URL}/api/learning', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLearningPaths(response.data);
    } catch (error) {
      console.error('Error fetching learning paths', error);
      // Fallback to sample data
      setLearningPaths([
        { id: 1, syllabus_title: 'Mathematics Fundamentals', current_topic_title: 'Algebra Basics', progress_percentage: 75 },
        { id: 2, syllabus_title: 'Introduction to Physics', current_topic_title: 'Newtonian Mechanics', progress_percentage: 50 }
      ]);
    }
  };

  const fetchHomework = async () => {
    // Mock homework data for now
    setHomework([
      { id: 1, title: 'Math Assignment 1', description: 'Complete exercises 1-10', due_date: '2025-01-15', status: 'pending' },
      { id: 2, title: 'Science Project', description: 'Research and present on renewable energy', due_date: '2025-01-20', status: 'submitted' }
    ]);
  };

  const handleEnroll = async (courseId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post('${config.API_BASE_URL}/api/enrollments', { course_id: courseId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchEnrolledCourses();
      fetchAvailableCourses();
    } catch (error) {
      console.error('Error enrolling', error);
    }
  };

  const handleUnenroll = async (courseId) => {
    if (window.confirm('Are you sure you want to unenroll from this course? You will lose access to all course materials.')) {
      const token = localStorage.getItem('token');
      try {
        await axios.delete(`${config.API_BASE_URL}/api/enrollments/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchEnrolledCourses();
        fetchAvailableCourses();
        alert('Successfully unenrolled from course');
      } catch (error) {
        console.error('Error unenrolling', error);
        alert('Error unenrolling from course');
      }
    }
  };

  const handleSelectCourse = async (course) => {
    setSelectedCourse(course);
    const token = localStorage.getItem('token');

    // Fetch videos for this course
    try {
      const videoResponse = await axios.get(`${config.API_BASE_URL}/api/videos/course/${course.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseVideos(videoResponse.data);
    } catch (error) {
      console.error('Error fetching course videos', error);
      setCourseVideos([]);
    }

    // Fetch homework for this course
    try {
      const homeworkResponse = await axios.get(`${config.API_BASE_URL}/api/homework/course/${course.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseHomework(homeworkResponse.data);
    } catch (error) {
      console.error('Error fetching course homework', error);
      setCourseHomework([]);
    }
  };

  const handleChat = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post('${config.API_BASE_URL}/api/chat', { message: chatMessage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatHistory([...chatHistory, { user: chatMessage, ai: response.data.response }]);
      setChatMessage('');
    } catch (error) {
      console.error('Error chatting', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.href = '/';
  };

  return (
    <div className="container-fluid">
      {/* Dashboard Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">🎓 Student Dashboard</h2>
                <p className="text-muted mb-0">Track your learning progress and access course materials</p>
              </div>
              <button className="btn btn-outline-danger" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>{enrolledCourses.length}</h3>
                <p className="mb-0">Enrolled Courses</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>78%</h3>
                <p className="mb-0">Overall Progress</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>{homework.length}</h3>
                <p className="mb-0">Assignments</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>92%</h3>
                <p className="mb-0">Average Score</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs" id="studentTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
                    <i className="fas fa-tachometer-alt me-2"></i>Dashboard
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'courses' ? 'active' : ''}`} onClick={() => setActiveTab('courses')}>
                    <i className="fas fa-book me-2"></i>My Courses
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'learning' ? 'active' : ''}`} onClick={() => setActiveTab('learning')}>
                    <i className="fas fa-route me-2"></i>Learning Path
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'progress' ? 'active' : ''}`} onClick={() => setActiveTab('progress')}>
                    <i className="fas fa-chart-line me-2"></i>Progress
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'homework' ? 'active' : ''}`} onClick={() => setActiveTab('homework')}>
                    <i className="fas fa-tasks me-2"></i>Homework
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              {/* Tab Content */}
              {activeTab === 'dashboard' && (
                <div className="fade-in">
                  <h4>Welcome to your Student Dashboard</h4>
                  <p>Access your courses, track progress, and get help from our AI assistant.</p>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card border-primary">
                        <div className="card-body">
                          <h5 className="card-title text-primary">📚 Course Access</h5>
                          <p className="card-text">Browse and enroll in available courses.</p>
                          <button className="btn btn-primary" onClick={() => setActiveTab('courses')}>View Courses</button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-success">
                        <div className="card-body">
                          <h5 className="card-title text-success">🤖 AI Assistant</h5>
                          <p className="card-text">Get help with your studies from our AI chatbot.</p>
                          <button className="btn btn-success" onClick={() => setActiveTab('homework')}>Chat with AI</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'courses' && (
                <div className="fade-in">
                  <div className="row">
                    <div className="col-md-6">
                      <h4>Enrolled Courses</h4>
                      <div className="row">
                        {enrolledCourses.map(course => (
                          <div key={course.id} className="col-md-12 mb-3">
                            <div className={`card h-100 shadow-sm ${selectedCourse && selectedCourse.id === course.id ? 'border-primary' : ''}`}>
                              <div className="card-body">
                                <h6>{course.title}</h6>
                                <p>{course.description}</p>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="badge bg-success">Enrolled</span>
                                  <small className="text-muted">Click to view content</small>
                                </div>
                                <div className="d-flex gap-2 mt-2">
                                  <button className="btn btn-outline-primary btn-sm" onClick={() => handleSelectCourse(course)}>View Course</button>
                                  <button className="btn btn-outline-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleUnenroll(course.id); }}>Unenroll</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h4>Available Courses</h4>
                      <div className="row">
                        {availableCourses.map(course => (
                          <div key={course.id} className="col-md-12 mb-3">
                            <div className="card h-100 shadow-sm">
                              <div className="card-body">
                                <h6>{course.title}</h6>
                                <p>{course.description}</p>
                                <button className="btn btn-primary btn-sm" onClick={() => handleEnroll(course.id)}>Enroll</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Selected Course Content */}
                  {selectedCourse && (
                    <div className="mt-4">
                      <h4>📚 {selectedCourse.title} - Course Content</h4>
                      <div className="row">
                        <div className="col-md-6">
                          <h5>🎥 Course Videos</h5>
                          {courseVideos.length > 0 ? (
                            <div className="row">
                              {courseVideos.map(video => (
                                <div key={video.id} className="col-md-12 mb-3">
                                  <div className="card">
                                    <div className="card-body">
                                      <h6>{video.title || 'Untitled Video'}</h6>
                                      <p>{video.url}</p>
                                      <button className="btn btn-primary btn-sm">Watch Video</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted">No videos available for this course yet.</p>
                          )}
                        </div>
                        <div className="col-md-6">
                          <h5>📝 Course Homework</h5>
                          {courseHomework.length > 0 ? (
                            <div className="row">
                              {courseHomework.map(hw => (
                                <div key={hw.id} className="col-md-12 mb-3">
                                  <div className="card">
                                    <div className="card-body">
                                      <h6>{hw.title}</h6>
                                      <p>{hw.description}</p>
                                      <p className="text-muted">Due: {hw.due_date}</p>
                                      <button className="btn btn-success btn-sm">Start Assignment</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted">No homework available for this course yet.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'learning' && (
                <div className="fade-in">
                  <h4>Personalized Learning Paths</h4>
                  <div className="row">
                    {learningPaths.map(path => (
                      <div key={path.id} className="col-md-6 mb-3">
                        <div className="card">
                          <div className="card-body">
                            <h6>{path.syllabus_title}</h6>
                            <p>Current Topic: {path.current_topic_title || 'Not started'}</p>
                            <div className="progress mb-2">
                              <div className="progress-bar" style={{ width: `${path.progress_percentage}%` }}>
                                {path.progress_percentage}%
                              </div>
                            </div>
                            <button className="btn btn-outline-primary btn-sm">Continue Learning</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'progress' && (
                <div className="fade-in">
                  <h4>Progress Tracking & Analytics</h4>
                  <div className="row">
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>78%</h3>
                            <p className="mb-0">Overall Progress</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>92%</h3>
                            <p className="mb-0">Average Score</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>15</h3>
                            <p className="mb-0">Completed Topics</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>5</h3>
                            <p className="mb-0">Active Courses</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h5>Recent Activity</h5>
                    <ul className="list-group">
                      <li className="list-group-item">Completed Math Quiz - Score: 95%</li>
                      <li className="list-group-item">Finished Science Video Lecture</li>
                      <li className="list-group-item">Submitted History Essay</li>
                      <li className="list-group-item">Started New Course: Advanced Physics</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'homework' && (
                <div className="fade-in">
                  <div className="row">
                    <div className="col-md-8">
                      <h4>Homework & Assignments</h4>
                      <div className="row">
                        {homework.map(hw => (
                          <div key={hw.id} className="col-md-12 mb-3">
                            <div className="card">
                              <div className="card-body">
                                <h6>{hw.title}</h6>
                                <p>{hw.description}</p>
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">Due: {hw.due_date}</small>
                                  <div className="d-flex gap-2">
                                    {hw.status === 'pending' ? (
                                      <>
                                        <input type="file" className="form-control form-control-sm" />
                                        <button className="btn btn-success btn-sm">Submit</button>
                                      </>
                                    ) : (
                                      <span className="badge bg-success">Submitted</span>
                                    )}
                                    <button className="btn btn-outline-info btn-sm">Download Materials</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <h4>AI Study Assistant</h4>
                      <div className="border rounded p-3" style={{ height: '400px', overflowY: 'scroll' }}>
                        {chatHistory.map((msg, idx) => (
                          <div key={idx} className="mb-2">
                            <strong>You:</strong> {msg.user}<br />
                            <strong>AI:</strong> {msg.ai}
                          </div>
                        ))}
                      </div>
                      <div className="input-group mt-2">
                        <input type="text" className="form-control" value={chatMessage} onChange={(e) => setChatMessage(e.target.value)} placeholder="Ask for help..." />
                        <button className="btn btn-primary" onClick={handleChat}>Send</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Student;